import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Trash2, Database, Shield, Loader2 } from "lucide-react";
import { usePreviewUserDeletion, usePermanentlyDeleteUserData } from "@/hooks/use-delete-user-data";

interface PermanentDeleteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    linked_user?: { id: string } | null;
  };
}

export const PermanentDeleteModal = ({ open, onOpenChange, contact }: PermanentDeleteModalProps) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [confirmText, setConfirmText] = useState("");
  
  const userId = contact.linked_user?.id || null;
  const { data: preview, isLoading: previewLoading } = usePreviewUserDeletion(userId);
  const deleteUser = usePermanentlyDeleteUserData();
  
  const confirmPhrase = "DELETE";
  const canProceedToStep2 = preview?.can_delete === true;
  const canProceedToStep3 = step === 2;
  const canDelete = step === 3 && confirmText === confirmPhrase;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setConfirmText("");
    }
  }, [open]);

  const handleDelete = async () => {
    if (!userId || !canDelete) return;
    
    try {
      await deleteUser.mutateAsync(userId);
      onOpenChange(false);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const getTotalDeleted = () => {
    if (!preview?.records_to_delete) return 0;
    return Object.values(preview.records_to_delete).reduce((a, b) => a + b, 0);
  };

  const getTotalNullified = () => {
    if (!preview?.records_to_nullify) return 0;
    return Object.values(preview.records_to_nullify).reduce((a, b) => a + b, 0);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Permanently Delete User Data
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              {/* User info */}
              <div className="bg-muted/50 rounded-lg p-3 border">
                <p className="font-medium text-foreground">
                  {contact.first_name} {contact.last_name}
                </p>
                <p className="text-sm text-muted-foreground">{contact.email}</p>
              </div>

              {/* Step 1: Preview */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Step 1 of 3
                    </Badge>
                    <span className="text-sm font-medium">Review what will be deleted</span>
                  </div>

                  {previewLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : !userId ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-amber-800 text-sm">
                        This contact has no linked user account. Use the regular delete function instead.
                      </p>
                    </div>
                  ) : !preview?.can_delete ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-amber-800 text-sm font-medium">User must be archived first</p>
                      <p className="text-amber-700 text-sm mt-1">
                        Archive this user before permanently deleting their data.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Records to delete */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                          <Trash2 className="h-4 w-4" />
                          Records to be permanently deleted ({getTotalDeleted()})
                        </div>
                        <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 space-y-1">
                          {preview?.records_to_delete && Object.entries(preview.records_to_delete).map(([key, count]) => (
                            count > 0 && (
                              <div key={key} className="flex justify-between text-sm">
                                <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                                <span className="font-mono text-destructive">{count}</span>
                              </div>
                            )
                          ))}
                        </div>
                      </div>

                      {/* Records to nullify */}
                      {getTotalNullified() > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-amber-600">
                            <Database className="h-4 w-4" />
                            References to be cleared (data preserved) ({getTotalNullified()})
                          </div>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                            {preview?.records_to_nullify && Object.entries(preview.records_to_nullify).map(([key, count]) => (
                              count > 0 && (
                                <div key={key} className="flex justify-between text-sm">
                                  <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                                  <span className="font-mono text-amber-700">{count}</span>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Auth record notice */}
                      <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <Shield className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                        <span className="text-blue-800">
                          The user's authentication record will be preserved. They will see "Account not found" if they try to log in.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Confirmation */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Step 2 of 3
                    </Badge>
                    <span className="text-sm font-medium">Confirm your intent</span>
                  </div>

                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 space-y-3">
                    <p className="text-sm text-destructive font-medium">
                      ⚠️ This action is IRREVERSIBLE
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>{getTotalDeleted()} records will be permanently deleted</li>
                      <li>{getTotalNullified()} historical references will be cleared</li>
                      <li>This cannot be undone</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Step 3: Type to confirm */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">
                      Step 3 of 3
                    </Badge>
                    <span className="text-sm font-medium">Final confirmation</span>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Type <span className="font-mono font-bold text-destructive">{confirmPhrase}</span> to permanently delete this user's data:
                    </p>
                    <Input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                      placeholder={confirmPhrase}
                      className="font-mono text-center"
                      autoFocus
                    />
                  </div>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel disabled={deleteUser.isPending}>Cancel</AlertDialogCancel>
          
          {step === 1 && canProceedToStep2 && (
            <Button 
              variant="outline" 
              onClick={() => setStep(2)}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              I understand, continue
            </Button>
          )}
          
          {step === 2 && (
            <Button 
              variant="outline" 
              onClick={() => setStep(3)}
              className="border-destructive/50 text-destructive hover:bg-destructive/10"
            >
              Proceed to final step
            </Button>
          )}
          
          {step === 3 && (
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={!canDelete || deleteUser.isPending}
            >
              {deleteUser.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
